<template>
  <table-page
    v-model:search-term="searchTerm"
    v-model:selected-filters="selectedFilters"
    search-placeholder="Código de autorização ou valor"
    title="Cartão de crédito"
    :loading="isSummaryLoading"
    @clean-filter="onCleanFilter"
    @submit-filters="onSubmitFilters"
  >
    <template #bg-date-filter>
      <bg-date-filter
        v-model:start-date="period.startDate"
        v-model:end-date="period.endDate"
        full-width
        @update:start-date="onSubmitFilters"
        @update:end-date="onSubmitFilters"
      />
    </template>

    <template #toolbar-buttons>
      <card-receivables-export-button
        :statuses="selectedFilters?.status"
        :search="searchTerm"
        :receive-date-gte="period.startDate.toISOString()"
        :receive-date-lte="period.endDate.toISOString()"
      />
    </template>

    <template #header>
      <bg-summary-group>
        <bg-summary
          title="A receber"
          icon="calendar_today"
          variant="warning"
          layout="stacked"
          :value="centsToReal(summary.scheduled?.total_amount)"
          :loading="isSummaryLoading"
        />
        <bg-summary
          title="Recebidos"
          icon="check-price"
          variant="success"
          layout="stacked"
          :value="centsToReal(summary.received?.total_amount)"
          :loading="isSummaryLoading"
        />
        <bg-summary
          title="Cancelados"
          icon="cancel"
          variant="neutral"
          layout="stacked"
          :value="centsToReal(summary.canceled?.total_amount)"
          :loading="isSummaryLoading"
        />
        <bg-summary
          title="Devolvidos"
          icon="back-arrow"
          variant="info"
          layout="stacked"
          :value="centsToReal(summary.refunded?.total_amount)"
          :loading="isSummaryLoading"
        />
      </bg-summary-group>
    </template>

    <template #bg-filters>
      <bg-filter
        key="status"
        label="Status"
      >
        <bg-filter-option
          v-for="(statusName, statusKey) in statusFilter"
          :key="statusKey"
          :label="statusName"
        />
      </bg-filter>
    </template>

    <template #content>
      <tr-table disable-click>
        <thead>
          <tr-tr>
            <tr-th width="12%">
              Status
            </tr-th>
            <tr-th width="15%">
              Data da transação
            </tr-th>
            <tr-th width="15%">
              Data do recebimento
            </tr-th>
            <tr-th width="15%">
              Cod. de autorização
            </tr-th>
            <tr-th width="10%">
              Parcelamento
            </tr-th>
            <tr-th width="15%">
              Valor bruto
            </tr-th>
            <tr-th width="25%">
              Valor líquido
            </tr-th>
          </tr-tr>
        </thead>
        <tbody>
          <tr-tr
            v-for="receivable in receivables"
            :key="receivable.id"
          >
            <tr-td>
              <card-receivable-status-badge :status="receivable.status" />
            </tr-td>
            <tr-td>
              {{ formatDateTime(receivable.transaction.date) }}
            </tr-td>
            <tr-td>
              <template v-if="receivable.receive_date">
                {{ formatDate(receivable.receive_date) }}
              </template>
              <template v-else-if="receivable.expected_receive_date">
                {{ formatDate(receivable.expected_receive_date) }}
              </template>
              <template v-else>
                ---
              </template>
            </tr-td>
            <tr-td>
              {{ receivable.authorization_code }}
            </tr-td>
            <tr-td>
              {{
                receivable.transaction.installments === 1
                  ? 'À vista'
                  : `${receivable.transaction.installments}x`
              }}
            </tr-td>
            <tr-td>
              {{ centsToReal(receivable.gross_amount) }}
            </tr-td>
            <tr-td>
              {{ centsToReal(receivable.net_amount) }}
            </tr-td>
          </tr-tr>
        </tbody>
      </tr-table>
      <tr-numeric-pagination
        class="payins-pagination"
        :current-page="pagination.currentPage"
        :page-size="pagination.itemsPerPage"
        :total="pagination.totalItems"
        :on-current-page-change="onPageChange"
      />
    </template>
  </table-page>
</template>

<script setup>
import { ref, watch } from 'vue';
import TablePage from '@/commons/components/presentational/TablePage.vue';
import { BgDateFilter, BgFilter, BgFilterOption, BgSummary, BgSummaryGroup } from '@transfeeradev/bridge';
import moment from '@transfeeradev/moment';
import { useDebounceFn } from '@vueuse/core';
import { listCardReceivables, getCardReceivablesSummary } from '@/apps/payin/card-receivables/services/cardReceivables';
import { useErrorHandler } from '@/commons/composables/useErrorHandler';
import { centsToReal } from '@/commons/formatters/currency';
import formatDate, { formatDateTime } from '@/commons/formatters/formatDate';
import CardReceivableStatusBadge from '@/apps/payin/card-receivables/components/CardReceivableStatusBadge.vue';
import CardReceivablesExportButton from '@/apps/payin/card-receivables/components/CardReceivablesExportButton.vue';

const { handleError } = useErrorHandler();

const emit = defineEmits(['loading']);
const /** @type {Ref<CardReceivable[]>} */ receivables = ref([]);
const summary = ref({});

const pagination = ref({ totalItems: null, itemsPerPage: 10, currentPage: 0 });
const searchTerm = ref();

const statusFilter = ref({
  scheduled: 'A receber',
  received: 'Recebidos',
  canceled: 'Cancelados'
});

const isSummaryLoading = ref(true);
const selectedFilters = ref({
  status: {}
});

const period = ref({
  startDate: moment().startOf('month').toDate(),
  endDate: moment().endOf('month').toDate()
});

const onPageChange = page => {
  pagination.value.currentPage = page;
};

const onSubmitFilters = () => {
  pagination.value.currentPage = 0;
  fetchReceivables();
  fetchSummary();
};

const onCleanFilter = () => {
  selectedFilters.value.status = {};
  fetchReceivables();
  fetchSummary();
};

const fetchReceivables = useDebounceFn(async () => {
  try {
    const response = await listCardReceivables({
      page: pagination.value.currentPage,
      pageSize: pagination.value.itemsPerPage,
      statuses: selectedFilters.value.status,
      search: searchTerm.value,
      startDate: period.value.startDate,
      endDate: period.value.endDate
    });

    receivables.value = response.items;
    pagination.value.itemsPerPage = response.metadata.pagination.itemsPerPage;
    pagination.value.totalItems = response.metadata.pagination.totalItems;
  } catch (e) {
    handleError(e, 'Ocorreu um erro ao buscar os recebíveis');
  } finally {
    emit('loading', false);
  }
}, 300);

const fetchSummary = useDebounceFn(async () => {
  isSummaryLoading.value = true;
  try {
    const response = await getCardReceivablesSummary({
      statuses: selectedFilters.value.status,
      search: searchTerm.value,
      startDate: period.value.startDate,
      endDate: period.value.endDate
    });

    summary.value = response;
  } catch (e) {
    handleError(e, 'Ocorreu um erro ao buscar os totalizadores');
  } finally {
    isSummaryLoading.value = false;
  }
}, 300);

watch([() => statusFilter.value, () => period.value], () => {
  fetchReceivables();
  fetchSummary();
});

watch([() => pagination.value.currentPage], () => {
  fetchReceivables();
});

fetchReceivables();
fetchSummary();
</script>

<style scoped>
.payins-pagination {
  margin-top: var(--bg-spacing-sm) !important;
}
</style>
